import { deserializeAs } from 'cerialize';

// The driver app (v3) writes `partner_termination.reason` as its internal snake_case code, while
// `termination_reason` keeps the legacy prose. This table is the driver's own code -> prose map
// (`resupply-driver-native-v3/src/api/terminationReasons.ts`), byte-identical, curly apostrophes included.
export const PARTNER_TERMINATION_REASON_LABELS: Record<string, string> = {
  // decline
  too_small: 'This pickup is too small to be worth it',
  cant_haul_items: 'I can’t haul these items',
  too_much_travel: 'The pickup requires too much travel time',
  description_mismatch: 'The detailed pickup description doesn’t match the quote',
  overbooked_away: 'I am overbooked/away',
  // cancel
  donor_declined_cost: 'The donor declined the cost',
  donor_unresponsive: 'The donor was unresponsive',
  could_not_accept_items: 'I could not accept these items',
  // reschedule
  optimize_route: 'I need to group more donations to optimize my route',
  overbooked: 'I am overbooked',
  charity_closed: 'The charity will be closed on the day of the pickup',
  // shared by decline + reschedule
  truck_fleet_issue: 'I have a truck/fleet issue',
  other: 'Other',
};

export function partnerTerminationReasonLabel(reason?: string | null): string | null {
  if (!reason) {
    return null;
  }
  return PARTNER_TERMINATION_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

// After the decline reason the driver answers ONE follow-up question, and the reason decides which:
// a minimum quote (`amount`), a date (`date`) or a set of items (`item_ids`). The question text is
// per reason, same as the driver app asks it.
export type PartnerTerminationFollowUpKind = 'amount' | 'date' | 'items';

export interface PartnerTerminationFollowUp {
  kind: PartnerTerminationFollowUpKind;
  question: string;
}

const FOLLOW_UPS: Record<string, PartnerTerminationFollowUp> = {
  too_small: { kind: 'amount', question: 'Minimum quote to make this job profitable' },
  too_much_travel: { kind: 'amount', question: 'Minimum quote to make this job profitable' },
  cant_haul_items: { kind: 'items', question: 'Items the partner is unable to haul' },
  description_mismatch: { kind: 'items', question: 'Items that did not match the description' },
  overbooked_away: { kind: 'date', question: 'Earliest available date for this pickup' },
  truck_fleet_issue: { kind: 'date', question: 'Truck expected back in service' },
};

// Deserialize-only, like PartnerAssignment: the API appends an entry per declining partner and the
// trail must never be echoed back on a donation update. Oldest first; the newest is the last entry.
export class PartnerTermination implements IPartnerTermination {
  @deserializeAs('partner_id') partnerId?: string | null;
  @deserializeAs('reason') reason?: string | null;
  @deserializeAs('sub_reason') subReason?: string | null;
  @deserializeAs('amount') amount?: number | null;
  @deserializeAs('date') date?: string | null;
  @deserializeAs('item_ids') itemIds?: string[] | null;

  constructor(payload?: IPartnerTermination) {
    this.partnerId = payload?.partnerId?.toString() || null;
    this.reason = payload?.reason ?? null;
    this.subReason = payload?.subReason ?? null;
    this.amount = payload?.amount ?? null;
    this.date = payload?.date ?? null;
    this.itemIds = payload?.itemIds?.map((id) => id.toString()) ?? null;
  }

  get reasonLabel(): string | null {
    return partnerTerminationReasonLabel(this.reason);
  }

  // The question this reason asks, only when the driver actually answered it (an unanswered
  // follow-up is an absent key on the wire, never an empty value).
  get followUp(): PartnerTerminationFollowUp | null {
    const followUp = this.reason ? FOLLOW_UPS[this.reason] : undefined;
    if (!followUp) return null;
    if (followUp.kind === 'amount' && !this.amount) return null;
    if (followUp.kind === 'date' && !this.date) return null;
    if (followUp.kind === 'items' && !this.itemIds?.length) return null;
    return followUp;
  }
}

export interface IPartnerTermination {
  partnerId?: string | number | null;
  reason?: string | null;
  subReason?: string | null;
  amount?: number | null;
  date?: string | null;
  itemIds?: (string | number)[] | null;
}
